import React, { useEffect, useMemo, useState } from "react";
import {
  AccountCircle,
  Lock,
  WorkspacePremiumOutlined,
  WarehouseOutlined,
  LocalShippingOutlined,
  HandshakeOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CssBaseline,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import useAdd from "../../../api/useAdd";
import { useAuth } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";

const convertToEnglishDigits = (value = "") => {
  const map = {
    "\u06f0": "0",
    "\u06f1": "1",
    "\u06f2": "2",
    "\u06f3": "3",
    "\u06f4": "4",
    "\u06f5": "5",
    "\u06f6": "6",
    "\u06f7": "7",
    "\u06f8": "8",
    "\u06f9": "9",
    "\u0660": "0",
    "\u0661": "1",
    "\u0662": "2",
    "\u0663": "3",
    "\u0664": "4",
    "\u0665": "5",
    "\u0666": "6",
    "\u0667": "7",
    "\u0668": "8",
    "\u0669": "9",
  };

  return String(value).replace(/[\u06f0-\u06f9\u0660-\u0669]/g, (digit) => map[digit] || digit);
};

const cornerPattern = `url("data:image/svg+xml,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="240" height="240" viewBox="0 0 240 240" fill="none">
    <g opacity="0.36" stroke="#2E5D34" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M34 54c10-11 24-13 34-4-10 11-24 13-34 4Z" />
      <path d="M47 54c6 5 9 10 11 16" />
      <path d="M184 38c10-11 24-13 34-4-10 11-24 13-34 4Z" />
      <path d="M197 38c6 5 9 10 11 16" />
      <path d="M28 176c11-7 26-5 34 6-11 7-26 5-34-6Z" />
      <path d="M41 179c5 6 8 11 9 17" />
      <path d="M168 172c11-7 26-5 34 6-11 7-26 5-34-6Z" />
      <path d="M181 175c5 6 8 11 9 17" />
    </g>
    <g opacity="0.18" fill="none" stroke="#C79A3B" stroke-width="1.1">
      <ellipse cx="116" cy="76" rx="9" ry="14" />
      <ellipse cx="134" cy="88" rx="9" ry="14" />
      <ellipse cx="98" cy="164" rx="10" ry="15" />
      <ellipse cx="156" cy="150" rx="10" ry="15" />
    </g>
  </svg>
`)}")`;

const heroFeatures = [
  "Premium Quality Products",
  "Smart Inventory Management",
  "Supplier & Customer Management",
  "Purchase & Sales Tracking",
  "Warehouse Management",
  "Financial Reports",
  "Secure Business Operations",
];

const featureCards = [
  { icon: <WorkspacePremiumOutlined fontSize="small" />, label: "Premium Quality" },
  { icon: <WarehouseOutlined fontSize="small" />, label: "Warehouse" },
  { icon: <LocalShippingOutlined fontSize="small" />, label: "Fast Delivery" },
  { icon: <HandshakeOutlined fontSize="small" />, label: "Trusted Business" },
];

const loginGreen = "#3f6b37";
const loginGreenDark = "#2f552a";
const loginGreenLight = "#5b854f";
const heroAccent = "#d08b2a";
const heroAccentSoft = "#e0aa47";

const floatY = `
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(0, -28px, 0) scale(1.03); }
`;

const floatYReverse = `
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(0, 22px, 0) scale(0.98); }
`;

const Page = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  padding: theme.spacing(2),
  position: "relative",
  isolation: "isolate",
  overflow: "hidden",
  background:
    "radial-gradient(circle at 16% 18%, rgba(199, 154, 59, 0.34) 0%, rgba(199, 154, 59, 0) 28%), radial-gradient(circle at 84% 22%, rgba(63, 107, 55, 0.16) 0%, rgba(63, 107, 55, 0) 30%), radial-gradient(circle at 50% 92%, rgba(139, 82, 36, 0.18) 0%, rgba(139, 82, 36, 0) 34%), linear-gradient(135deg, #f9f4ea 0%, #f4ebdd 38%, #fcfaf5 68%, #efe2cf 100%)",
  fontFamily: '"Georgia", "Times New Roman", serif',
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage:
      `${cornerPattern}, radial-gradient(circle at 15% 20%, rgba(46,93,52,0.05) 0 1.5px, transparent 2.5px), radial-gradient(circle at 84% 78%, rgba(199,154,59,0.05) 0 1.5px, transparent 2.5px)`,
    backgroundSize: "240px 240px, 170px 170px, 170px 170px",
    backgroundPosition: "top left, top left, bottom right",
    backgroundRepeat: "no-repeat, repeat, repeat",
    opacity: 0.18,
    pointerEvents: "none",
    zIndex: 0,
  },
}));

const AmbientOrbs = styled(Box)(() => ({
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  overflow: "hidden",
  zIndex: 0,
  display: "none",
}));

const FloatingOrb = styled(Box, {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ variant }) => ({
  position: "absolute",
  borderRadius: "50%",
  filter: "blur(0px)",
  opacity: 0.72,
  mixBlendMode: "normal",
  background:
    variant === "gold"
      ? "radial-gradient(circle at 35% 35%, rgba(255, 220, 156, 0.95) 0%, rgba(199, 154, 59, 0.55) 35%, rgba(199, 154, 59, 0.06) 72%, rgba(199, 154, 59, 0) 100%)"
      : variant === "green"
      ? "radial-gradient(circle at 35% 35%, rgba(120, 168, 94, 0.88) 0%, rgba(63, 107, 55, 0.42) 36%, rgba(63, 107, 55, 0.06) 72%, rgba(63, 107, 55, 0) 100%)"
      : "radial-gradient(circle at 35% 35%, rgba(225, 177, 109, 0.9) 0%, rgba(139, 82, 36, 0.42) 36%, rgba(139, 82, 36, 0.05) 72%, rgba(139, 82, 36, 0) 100%)",
  boxShadow:
    variant === "gold"
      ? "0 0 60px rgba(199, 154, 59, 0.20)"
      : variant === "green"
      ? "0 0 60px rgba(63, 107, 55, 0.18)"
      : "0 0 60px rgba(139, 82, 36, 0.18)",
  animation: `${variant === "green" ? floatYReverse : floatY} ${variant === "gold" ? "12s" : variant === "green" ? "15s" : "18s"} ease-in-out infinite`,
}));

const Shell = styled(Card)(({ theme }) => ({
  width: "min(1180px, 94vw)",
  minHeight: "min(86vh, 840px)",
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  overflow: "hidden",
  borderRadius: 34,
  border: `1px solid ${alpha("#d8ccb7", 0.68)}`,
  boxShadow: "0 28px 80px rgba(54, 44, 22, 0.16)",
  background: "#fffaf4",
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    minHeight: "auto",
  },
}));

const HeroPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  minHeight: 520,
  background:
    "linear-gradient(180deg, rgba(34,18,8,0.24) 0%, rgba(34,18,8,0.68) 100%), url('/login-image.png') center/cover no-repeat",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(135deg, rgba(25,13,5,0.24) 0%, rgba(25,13,5,0.06) 30%, rgba(25,13,5,0.18) 100%)",
  },
  [theme.breakpoints.down("md")]: {
    minHeight: 340,
  },
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: theme.spacing(3.5),
  color: "#fff",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.25),
  },
}));

const HeroCenter = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  textAlign: "left",
  paddingTop: "0.8rem",
}));

const Badge = styled(Box)(() => ({
  width: 44,
  height: 44,
  borderRadius: "50%",
  background: "rgba(255, 242, 217, 0.96)",
  boxShadow: "0 10px 20px rgba(0,0,0,0.08)",
  display: "grid",
  placeItems: "center",
}));

const HeroTitle = styled(Typography)(() => ({
  fontFamily: '"Georgia", "Times New Roman", serif',
  fontWeight: 700,
  lineHeight: 0.92,
  letterSpacing: -0.4,
}));

const HeroSubtitle = styled(Typography)(() => ({
  color: "rgba(255, 242, 219, 0.92)",
  lineHeight: 1.5,
  fontSize: 13,
}));

const HeroFeatureList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.1),
  display: "grid",
  gap: theme.spacing(1),
  width: "min(100%, 390px)",
}));

const HeroFeatureItem = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  color: "#fff6e8",
  fontSize: 13.5,
  lineHeight: 1.4,
}));

const CheckMark = styled(Box)(() => ({
  width: 20,
  height: 20,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  flex: "0 0 auto",
  border: `1px solid ${loginGreenLight}`,
  color: loginGreenLight,
  fontWeight: 800,
  fontSize: 13,
  background: "rgba(63, 107, 55, 0.18)",
}));

const FeatureGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
}));

const FeatureCard = styled(Box)(() => ({
  minHeight: 76,
  borderRadius: 18,
  padding: "10px 10px",
  textAlign: "center",
  color: "#fff4e1",
  background: alpha("#1f391c", 0.34),
  border: `1px solid ${alpha(loginGreenLight, 0.22)}`,
  boxShadow: "0 10px 24px rgba(18, 41, 16, 0.20)",
  backdropFilter: "blur(3px)",
}));

const FeatureIconWrap = styled(Box)(() => ({
  width: 28,
  height: 28,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  margin: "0 auto 8px",
  background: alpha(loginGreenLight, 0.16),
}));

const FormPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "grid",
  placeItems: "center",
  padding: theme.spacing(4.75),
  background:
    "linear-gradient(180deg, rgba(251,247,240,0.98) 0%, rgba(247,241,232,0.98) 100%)",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage:
      `${cornerPattern}, radial-gradient(circle at 18% 18%, rgba(46,93,52,0.04) 0 1.5px, transparent 2.5px), radial-gradient(circle at 80% 82%, rgba(199,154,59,0.04) 0 1.5px, transparent 2.5px)`,
    backgroundSize: "220px 220px, 160px 160px, 160px 160px",
    backgroundPosition: "top left, top left, bottom right",
    backgroundRepeat: "no-repeat, repeat, repeat",
    opacity: 0.22,
    pointerEvents: "none",
  },
}));

const LoginCard = styled(Card)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  width: "min(100%, 500px)",
  minHeight: 650,
  borderRadius: 28,
  background: alpha("#ffffff", 0.96),
  backdropFilter: "blur(14px) saturate(110%)",
  border: `1px solid ${alpha("#d8ccb7", 0.75)}`,
  boxShadow: "0 20px 48px rgba(58, 47, 28, 0.10)",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const LogoRing = styled(Box)(() => ({
  width: 72,
  height: 72,
  borderRadius: "50%",
  margin: "0 auto",
  display: "grid",
  placeItems: "center",
  background: `linear-gradient(180deg, ${loginGreenDark} 0%, ${loginGreen} 100%)`,
  boxShadow: "0 12px 26px rgba(31, 58, 28, 0.16)",
  border: "3px solid rgba(255,248,237,0.98)",
}));

const ProductName = styled(Typography)(() => ({
  fontFamily: '"Georgia", "Times New Roman", serif',
  color: loginGreenDark,
  fontWeight: 700,
  letterSpacing: 0.2,
  lineHeight: 1.03,
}));

const PrimaryButton = styled(Button)(() => ({
  borderRadius: 14,
  paddingTop: 12,
  paddingBottom: 12,
  fontWeight: 700,
  textTransform: "none",
  background: `linear-gradient(90deg, ${loginGreenDark} 0%, ${loginGreen} 100%)`,
  boxShadow: "0 14px 28px rgba(31, 58, 28, 0.22)",
  "&:hover": {
    background: `linear-gradient(90deg, ${loginGreenDark} 0%, ${loginGreenLight} 100%)`,
    boxShadow: "0 18px 32px rgba(31, 58, 28, 0.28)",
  },
}));

const SecondaryButton = styled(Button)(() => ({
  borderRadius: 14,
  paddingTop: 11,
  paddingBottom: 11,
  fontWeight: 700,
  textTransform: "none",
  borderColor: alpha(loginGreen, 0.45),
  color: loginGreenDark,
  background: "#fff",
  "&:hover": {
    borderColor: loginGreen,
    background: alpha(loginGreen, 0.04),
  },
}));

const InputSx = (theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 14,
    backgroundColor: "#fff",
    transition: "all .18s ease",
    "& fieldset": { borderColor: "rgba(175, 170, 158, 0.75)" },
    "&:hover fieldset": { borderColor: loginGreen },
    "&.Mui-focused": {
      boxShadow: `0 0 0 4px ${alpha(loginGreen, 0.08)}`,
    },
    "&.Mui-focused fieldset": {
      borderColor: loginGreen,
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: loginGreen,
  },
  "& .MuiInputAdornment-root": {
    color: "#6c6c6c",
  },
});

const TrustText = styled(Typography)(() => ({
  marginTop: 10,
  textAlign: "center",
  color: "#7a7468",
  fontSize: 11.5,
  letterSpacing: 0.2,
}));

const logoSvg = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" fill="none">
    <circle cx="48" cy="48" r="46" fill="white" opacity="0.1"/>
    <path d="M36 53c0-12 8-22 20-24 3 14-4 29-17 35-2-3-3-7-3-11Z" fill="#6FAE4A"/>
    <path d="M48 26c10 0 20 6 24 16-12 2-24-1-31-9 2-4 4-6 7-7Z" fill="#9CCB6B"/>
    <path d="M56 30c-1 18-10 32-26 40-2-3-3-6-3-10 0-15 12-27 29-30Z" fill="#7B3F1D" opacity="0.98"/>
    <path d="M60 34c9 2 15 8 18 17-8 1-15-1-20-7 1-4 1-7 2-10Z" fill="#A5623B"/>
    <path d="M46 61c4 7 11 11 20 11-1-10-5-17-14-21-2 3-4 6-6 10Z" fill="#5E2D18"/>
  </svg>
`;

function Login() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isRtl = useMemo(
    () =>
      (typeof i18n?.dir === "function" && i18n.dir() === "rtl") ||
      (typeof document !== "undefined" && document.documentElement?.dir === "rtl"),
    [i18n]
  );

  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [formData, setFormData] = useState({
    login: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const { handleAdd, resetForm, loading, responseData } = useAdd(
    "login",
    "",
    t("user.successFully"),
    t("user.failedLogin")
  );

  useEffect(() => {
    if (resetForm) {
      setFormData({ login: "", password: "" });
      auth.login(responseData);
      navigate("/shop/");
      setFormErrors({});
    }
  }, [resetForm, responseData, auth, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = {};
    if (!formData.login) errors.login = t("user.failedRequired");
    if (!formData.password) errors.password = t("user.failedRequired");

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    const payload = new FormData();
    Object.entries(formData).forEach(([key, value]) => payload.append(key, value));
    payload.append("remember", remember ? "1" : "0");
    handleAdd(payload);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "login" ? convertToEnglishDigits(value) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  return (
    <>
      <CssBaseline />
      <Page dir={isRtl ? "rtl" : "ltr"}>
        <AmbientOrbs>
          <FloatingOrb
            variant="gold"
            sx={{ width: 132, height: 132, top: "9%", left: "6%" }}
          />
          <FloatingOrb
            variant="green"
            sx={{ width: 170, height: 170, top: "18%", right: "10%" }}
          />
          <FloatingOrb
            variant="brown"
            sx={{ width: 96, height: 96, bottom: "12%", left: "18%" }}
          />
        </AmbientOrbs>
        <Shell
          component={motion.div}
          initial={{ opacity: 0, y: 24, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 80, damping: 16 }}
        >
          <HeroPanel component={motion.div} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <HeroContent>
              <HeroCenter>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
                  <Badge>
                    <img
                      src="/logo.jpg"
                      alt="Logo"
                      style={{ width: 28, height: 28, objectFit: "contain" }}
                    />
                  </Badge>
                  <Box sx={{ mt: 1.8 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: "1.65rem", sm: "2rem", md: "2.35rem" },
                        fontWeight: 800,
                        letterSpacing: 0.3,
                        lineHeight: 1.02,
                        color: "#fff6e8",
                        fontFamily: '"Georgia", "Times New Roman", serif',
                      }}
                    >
                      Habib Gilaman
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        display: "inline-block",
                        fontSize: { xs: "1rem", sm: "1.15rem" },
                        letterSpacing: 3.2,
                        color: "#f2b34e",
                        fontWeight: 700,
                      }}
                    >
                      D R I E D   F R U I T S
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 3.25, maxWidth: 480 }}>
                  <HeroTitle
                    component="h1"
                    sx={{
                      fontSize: { xs: "1.25rem", sm: "1.45rem", md: "1.7rem" },
                      maxWidth: 420,
                      whiteSpace: "nowrap",
                      color: "#fff8ee",
                    }}
                  >
                    Premium Dried Fruits
                    <br />
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        color: heroAccent,
                        fontSize: "0.92em",
                        lineHeight: 0.88,
                      }}
                    >
                      Management System
                    </Box>
                  </HeroTitle>
                  <Typography
                    sx={{
                      mt: 1.45,
                      color: heroAccentSoft,
                      fontSize: 13.5,
                      fontWeight: 500,
                    }}
                  >
                    Fresh Quality. Trusted Supply. Smart Management.
                  </Typography>
                  <Box
                    sx={{
                      width: 84,
                      height: 2,
                      mt: 1.8,
                      mb: 1.9,
                      borderRadius: 999,
                      background:
                        `linear-gradient(90deg, rgba(209,154,61,0), rgba(209,154,61,0.98), rgba(209,154,61,0))`,
                    }}
                  />
                  <HeroSubtitle sx={{ mt: 0, maxWidth: 385 }}>
                    Manage inventory, suppliers, customers, purchases, sales, warehouses,
                    deliveries, and financial operations from one powerful platform.
                  </HeroSubtitle>

                  <HeroFeatureList>
                    {heroFeatures.map((item) => (
                      <HeroFeatureItem key={item}>
                        <CheckMark>{"\u2713"}</CheckMark>
                        <span>{item}</span>
                      </HeroFeatureItem>
                    ))}
                  </HeroFeatureList>
                </Box>
              </HeroCenter>

              <FeatureGrid>
                {featureCards.map((item) => (
                  <FeatureCard key={item.label}>
                    <FeatureIconWrap>{item.icon}</FeatureIconWrap>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", fontWeight: 700, lineHeight: 1.15, color: "#fff7ea", fontSize: "0.74rem" }}
                    >
                      {item.label}
                    </Typography>
                  </FeatureCard>
                ))}
              </FeatureGrid>
            </HeroContent>
          </HeroPanel>

          <FormPanel component={motion.div} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <LoginCard elevation={0} component={motion.div} whileHover={{ y: -2 }}>
              <CardContent
                sx={{
                  p: { xs: 3.25, sm: 4.75 },
                  "&:last-child": { pb: { xs: 3.25, sm: 4.75 } },
                  minHeight: 650,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ textAlign: "center", mb: 2.4 }}>
                  <LogoRing>
                    <Box
                      component="span"
                      sx={{
                        width: 44,
                        height: 44,
                        display: "block",
                        background:
                          `center / contain no-repeat url("data:image/svg+xml,${encodeURIComponent(logoSvg)}")`,
                      }}
                    />
                  </LogoRing>

                  <ProductName
                    component="h2"
                    sx={{
                      mt: 1.5,
                      fontSize: { xs: "1.55rem", sm: "1.95rem" },
                    }}
                  >
                    Welcome Back
                  </ProductName>

                  <Typography
                    variant="body2"
                    sx={{ mt: 0.55, color: "#6f6a61", fontSize: 12.5 }}
                  >
                    Sign in to access your business dashboard.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 118,
                    height: 2,
                    mx: "auto",
                    mb: 2.1,
                    borderRadius: 999,
                    background:
                      "linear-gradient(90deg, rgba(199,154,59,0), rgba(199,154,59,0.95), rgba(199,154,59,0))",
                  }}
                />

                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <TextField
                    fullWidth
                    required
                    id="login"
                    name="login"
                    label="Email or Phone Number"
                    placeholder="Enter email or phone number"
                    value={formData.login}
                    onChange={handleChange}
                    error={!!formErrors.login}
                    helperText={formErrors.login}
                    autoComplete="username"
                    autoFocus
                    sx={{ mb: "20px", ...InputSx(theme) }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountCircle />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    required
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    autoComplete="current-password"
                    sx={{ mb: 1.25, ...InputSx(theme) }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            onClick={() => setShowPassword((value) => !value)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    sx={{
                      mt: 0.75,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                          sx={{ p: 0.45, color: loginGreen }}
                        />
                      }
                      label={<Typography variant="body2">Remember Me</Typography>}
                    />

                    <Typography
                      component={Link}
                      to="/reset-password"
                      sx={{
                        color: loginGreen,
                        textDecoration: "none",
                        fontSize: 13,
                        fontWeight: 700,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Forgot Password?
                    </Typography>
                  </Box>

                  <PrimaryButton
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    startIcon={loading ? null : <Lock />}
                    sx={{ mt: 1.35 }}
                  >
                    {loading ? "Loading..." : "Sign In"}
                  </PrimaryButton>




                  <Divider
                    sx={{
                      my: 1.35,
                      "&::before, &::after": { borderColor: "rgba(195, 189, 176, 0.9)" },
                    }}
                  >
                    <Typography variant="caption" sx={{ color: loginGreenDark, fontWeight: 700 }}>
                      or continue with
                    </Typography>
                  </Divider>

                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <SecondaryButton type="button" variant="outlined" fullWidth disabled={loading}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          mr: 1,
                          display: "grid",
                          placeItems: "center",
                          fontSize: 12,
                          fontWeight: 800,
                          color: "#4285F4",
                          border: "1px solid rgba(66,133,244,0.22)",
                          background: "rgba(66,133,244,0.05)",
                        }}
                      >
                        G
                      </Box>
                      Continue with Google
                    </SecondaryButton>
                    <SecondaryButton type="button" variant="outlined" fullWidth disabled={loading}>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 8px)", gap: "2px", mr: 1 }}>
                        <Box sx={{ width: 8, height: 8, bgcolor: "#f35325" }} />
                        <Box sx={{ width: 8, height: 8, bgcolor: "#81bc06" }} />
                        <Box sx={{ width: 8, height: 8, bgcolor: "#05a6f0" }} />
                        <Box sx={{ width: 8, height: 8, bgcolor: "#ffba08" }} />
                      </Box>
                      Continue with Microsoft
                    </SecondaryButton>
                  </Box>

                  <Box
                    sx={{
                      mt: "auto",
                      pt: 2.2,
                      textAlign: "center",
                    }}
                  >
                    <TrustText sx={{ mb: 0.25, mt: 0 }}>Powered by Afghan Cosmos IT Solutions</TrustText>
                    <Typography
                      variant="caption"
                      sx={{ color: "#8a816f", display: "block", fontSize: 11 }}
                    >
                      www.afghancosmos.com
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </LoginCard>
          </FormPanel>
        </Shell>
      </Page>
    </>
  );
}

export default Login;





